import { Box, Flex, Text, IconButton, HStack } from "@chakra-ui/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Conversation } from "../types/Conversation.type";
import { Message } from "../types/Message.type";
import { MessageBubble } from "./MessageBubble";
import { MessageComposer } from "./MessageComposer";
import { fetchMessagesForConversation, markConversationRead } from "../api";
import { FiCornerUpLeft } from "react-icons/fi";
import Tooltip from "components/Tooltip";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { UpdateConversationProps } from "../types/SharedProps.types";

interface ConversationViewProps {
	allowNewMessages?: boolean;
	conversation: Conversation;
	scrollOnLoad: boolean;
	showBackButton: boolean;
	showOnlyChat?: boolean;
	showThreadRef?: boolean;
	goBackOnMobile: () => void;
	onSendingMessage?: (isSending: boolean) => void;
}

/**
 * Displays all the messages for the given conversation
 * @param param0
 * @returns
 */
export const ConversationView: React.FC<ConversationViewProps & UpdateConversationProps> = ({
	allowNewMessages = true,
	conversation,
	showOnlyChat = false,
	showThreadRef = true,
	scrollOnLoad = false,
	showBackButton,
	goBackOnMobile,
	onSendingMessage,
	onConversationUpdated,
}) => {
	const [messages, setMessages] = useState<Message[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const endOfMessagesRef = useRef<HTMLDivElement>(null);
	const messagesLoadedForRef = useRef<number>(null);

	useEffect(() => {
		if (isLoading || messagesLoadedForRef.current === conversation.id) {
			return;
		}


		setIsLoading(true);
		fetchMessagesForConversation(conversation.id)
			.then((data) => {
				messagesLoadedForRef.current = conversation.id;
				setMessages(data);

				if ((conversation.unread_count ?? 0) === 0) {
					return;
				}

				markConversationRead(conversation.id).then((result) => {
					if (result) {
						onConversationUpdated({ ...conversation, unread_count: 0 })
					}
				});
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [conversation, isLoading, onConversationUpdated]);

	const onNewMessage = (message: Message) => {
		setMessages([...messages, message]);
	};

	useLayoutEffect(() => {
		// Scroll to bottom after messages are loaded
		if (scrollOnLoad && endOfMessagesRef.current) {
			endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [messages, scrollOnLoad]);

	if (isLoading) {
		return <LoadingSpinner
			label="Loading Messages..."
			fontSize="md"
			horizontal
			spinnerSize="md"
			verticalPadding={10}
		/>
	}

	return (
		<Flex
			direction="column"
			h="full"
			minW={0}
			w="full"
		>
			{!showOnlyChat && <Box
				p={3}
				className="border-b"
			>
				<HStack>
					{showBackButton && <Tooltip label="Go back to all conversations">
						<IconButton
							aria-label="go back to conversation view"
							display="flex"
							icon={
								<FiCornerUpLeft
									size={30}
									onClick={goBackOnMobile}
								/>
							}
						/>
					</Tooltip>}
					<Text fontWeight="bold">
						{showThreadRef && <>#{conversation.reference_id}</>} {conversation.subject}
					</Text>
				</HStack>
			</Box>}

			<Box
				flex="1"
				overflowY="auto"
				className="px-4 py-2"
			>
				{messages &&
					messages.map((msg) => (
						<MessageBubble
							isLoading={isLoading}
							key={msg.id}
							message={msg}
						/>
					))}
				<div ref={endOfMessagesRef} />
			</Box>

			{allowNewMessages && (
				<div className="border-t">
					<MessageComposer
						conversationId={conversation.id}
						onNewMessage={onNewMessage}
						onSendingMessage={onSendingMessage}
					/>
				</div>
			)}
		</Flex>
	);
};
